// "Their children's T1D" facet — conditions about the contact's children with
// T1D, emitted as FLAT child leaves of the top-level AND. The engine same-row-
// binds sibling child leaves of a group (see filter_engine), so the facet's
// conditions — and any other child rule beside them — match a single child row,
// without an isolating sub-group. Owns child.T1D_Stage__c, child.age and
// child.age_at_diagnosis. Numeric ranges are between-style (min and/or max);
// "exactly" is intentionally omitted for ages.
import { useMemo } from "react";
import { useExplorerStore } from "../../store/explorerStore";
import type { FilterGroup, FilterRule } from "../../types/inpact";
import { isGroup, ruleValueToStringArray } from "../../lib/filterLogic";
import { useT1dStages } from "../../hooks/useT1dStages";
import { StageCheckboxList } from "./StageCheckboxList";

export const CHILD_STAGE_FIELD = "child.T1D_Stage__c";
export const CHILD_AGE_FIELD = "child.age";
export const CHILD_DX_FIELD = "child.age_at_diagnosis";

export interface ChildT1dAvailability {
  stage: boolean;
  age: boolean;
  diagnosisAge: boolean;
}

const ALL_CHILD_T1D_FIELDS: ChildT1dAvailability = {
  stage: true,
  age: true,
  diagnosisAge: true,
};

interface Range {
  min: string;
  max: string;
}
interface Snapshot {
  stages: string[];
  age: Range;
  dx: Range;
}
const EMPTY: Snapshot = { stages: [], age: { min: "", max: "" }, dx: { min: "", max: "" } };

// The (field, operator) pairs the controls can reproduce exactly: stage via
// IN, the two ages via >= / <=. A leaf outside this set is never claimed, so a
// facet edit can't rewrite it (e.g. flip `!=`/`<` to `IN`/`<=`) or drop it.
const STAGE_OPS = new Set(["IN"]);
const RANGE_OPS = new Set([">=", "<="]);

function isOwnableLeaf(r: FilterRule): boolean {
  // Mirror filter_engine._is_same_row_child_leaf: `scope` (Own/Either) only has
  // contact meaning for child.T1D_Stage__c — a scoped stage leaf matches the
  // contact's own stage, so the facet must not adopt it. For the age fields
  // scope is inert (the backend ignores it), so it doesn't affect ownership there.
  if (r.field === CHILD_STAGE_FIELD) {
    const scope = (r as unknown as { scope?: string }).scope;
    return STAGE_OPS.has(r.op) && (scope == null || scope === "Child");
  }
  if (r.field === CHILD_AGE_FIELD || r.field === CHILD_DX_FIELD) return RANGE_OPS.has(r.op);
  return false;
}

/** The flat top-level child leaves this facet owns and can round-trip
 *  byte-for-byte: one leaf per control slot — child.T1D_Stage__c IN (default/
 *  Child scope only), child.age / child.age_at_diagnosis >= / <=. Claimed only
 *  under a top-level AND (the rail's composition). A slot holding two matching
 *  leaves is left foreign (the single control can't show/rebuild both). Foreign
 *  child conditions (unrepresentable ops, scoped stage, sub-groups) are left in
 *  place: with flat emission they stay same-row-bound with the facet's leaves as
 *  siblings of the top-level AND, so a facet edit never rewrites, drops, or
 *  cross-binds them. */
function findOwnedLeaves(group: FilterGroup): FilterRule[] {
  if (group.logic !== "AND") return [];
  const bySlot = new Map<string, FilterRule[]>();
  for (const r of group.rules) {
    if (isGroup(r)) continue;
    if (!isOwnableLeaf(r)) continue;
    const slot = `${r.field}:${r.op}`;
    const arr = bySlot.get(slot);
    if (arr) arr.push(r);
    else bySlot.set(slot, [r]);
  }
  const owned: FilterRule[] = [];
  for (const arr of bySlot.values()) if (arr.length === 1) owned.push(arr[0]);
  return owned;
}

/** Remove this facet's owned flat child leaves from a group, leaving foreign
 *  child conditions untouched. Internal to the facet's own edit round-trip. */
function withoutChildBlock(group: FilterGroup): FilterGroup {
  const owned = findOwnedLeaves(group);
  if (owned.length === 0) return group;
  const ownedSet = new Set<FilterRule | FilterGroup>(owned);
  return { ...group, rules: group.rules.filter((r) => !ownedSet.has(r)) };
}

/** A flat child leaf that "Has child with T1D = No" makes contradictory inside an
 *  AND: child.age / child.age_at_diagnosis (any op OR scope — the backend ignores
 *  scope for them, so a scoped age still evaluates as a child EXISTS), or a
 *  default/Child-scope child.T1D_Stage__c. A scoped (Own/Either) stage matches the
 *  CONTACT's own stage, not a child, so it is NOT contradictory and is kept. */
function isContradictoryChildLeaf(r: FilterRule): boolean {
  if (r.field === CHILD_AGE_FIELD || r.field === CHILD_DX_FIELD) return true;
  if (r.field === CHILD_STAGE_FIELD) {
    const scope = (r as unknown as { scope?: string }).scope;
    return scope == null || scope === "Child";
  }
  return false;
}

/** Remove the child conditions the facet governs that "Has child with T1D = No"
 *  makes contradictory (of ANY operator, incl. legacy "exactly"-mode `=` age), so
 *  setting "No" can't leave a rule that silently returns zero rows.
 *
 *  Recurses ONLY through AND groups: in an AND, a child EXISTS that can't be
 *  satisfied (no children) forces the whole group false — the silent zero-rows bug
 *  — so the leaf must go, and an AND sub-group emptied by pruning is dropped from
 *  its parent. OR / NOT sub-groups are left intact: there a dead child leaf is
 *  harmless (an always-false OR disjunct doesn't change the result, and removing it
 *  could drop that branch for other contacts; a NOT needs its single child). */
export function withoutChildConditions(group: FilterGroup): FilterGroup {
  if (group.logic !== "AND") return group;
  const rules: Array<FilterRule | FilterGroup> = [];
  for (const r of group.rules) {
    if (isGroup(r)) {
      if (r.logic === "AND") {
        const pruned = withoutChildConditions(r);
        // Drop an AND sub-group emptied by pruning (an empty AND is vacuously
        // true, so removing it is a no-op). Never drop OR/NOT sub-groups —
        // they are left exactly as-is, even if already empty (an empty OR is
        // false; dropping it would broaden the filter).
        if (pruned.rules.length > 0) rules.push(pruned);
      } else {
        rules.push(r);
      }
    } else if (!isContradictoryChildLeaf(r)) {
      rules.push(r);
    }
  }
  return { ...group, rules };
}

function rangeFor(rules: FilterRule[], field: string): Range {
  const ge = rules.find((r) => r.field === field && r.op === ">=");
  const le = rules.find((r) => r.field === field && r.op === "<=");
  return { min: ge ? String(ge.value ?? "") : "", max: le ? String(le.value ?? "") : "" };
}

function readSnapshot(group: FilterGroup): Snapshot {
  const owned = findOwnedLeaves(group);
  if (owned.length === 0) return EMPTY;
  const stageRule = owned.find((r) => r.field === CHILD_STAGE_FIELD);
  const stages = ruleValueToStringArray(stageRule?.value ?? null);
  return { stages, age: rangeFor(owned, CHILD_AGE_FIELD), dx: rangeFor(owned, CHILD_DX_FIELD) };
}

function betweenRules(field: string, r: Range): FilterRule[] {
  const out: FilterRule[] = [];
  const min = r.min === "" ? null : Number(r.min);
  const max = r.max === "" ? null : Number(r.max);
  if (min !== null && Number.isFinite(min)) out.push({ field, op: ">=", value: min });
  if (max !== null && Number.isFinite(max)) out.push({ field, op: "<=", value: max });
  return out;
}

function buildLeaves(s: Snapshot): FilterRule[] {
  const rules: FilterRule[] = [];
  if (s.stages.length) rules.push({ field: CHILD_STAGE_FIELD, op: "IN", value: s.stages });
  rules.push(...betweenRules(CHILD_AGE_FIELD, s.age));
  rules.push(...betweenRules(CHILD_DX_FIELD, s.dx));
  return rules;
}

export function ChildrenT1dFacet({
  disabled = false,
  availability = ALL_CHILD_T1D_FIELDS,
}: {
  disabled?: boolean;
  availability?: ChildT1dAvailability;
}) {
  const { filterGroup, setFilterGroup } = useExplorerStore();
  const { values: stageOptions, isLoading, error } = useT1dStages(CHILD_STAGE_FIELD);
  const snap = useMemo(() => readSnapshot(filterGroup), [filterGroup]);

  const commit = (next: Snapshot) => {
    // The rail composes facets as a top-level AND; ignore edits under OR/NOT
    // (the facet is blank there and appending would split disjuncts or corrupt
    // a NOT). Remove exactly the flat child leaves we read (findOwnedLeaves) and
    // re-add the rebuilt ones as flat siblings of the top-level AND, where the
    // engine same-row-binds them (and any foreign child rule beside them).
    if (filterGroup.logic !== "AND") return;
    const cleared = withoutChildBlock(filterGroup);
    setFilterGroup({ ...cleared, rules: [...cleared.rules, ...buildLeaves(next)] });
  };
  const toggleStage = (v: string) => {
    const stages = snap.stages.includes(v)
      ? snap.stages.filter((x) => x !== v)
      : [...snap.stages, v];
    commit({ ...snap, stages });
  };

  return (
    <fieldset
      className={`children-t1d-facet facet-subgroup${disabled ? " facet-subgroup--disabled" : ""}`}
      disabled={disabled}
      aria-label="Their children's T1D"
      aria-disabled={disabled || undefined}
      title={disabled ? "Disabled: Has child with T1D is set to No" : undefined}
      style={{ border: 0, margin: 0, padding: 0, minInlineSize: "auto" }}
    >
      <p className="children-t1d-facet__hint">Matched against a single child.</p>
      {availability.stage && (
        <>
          <div className="facet-subgroup__label">T1D stage</div>
          {isLoading && <p role="status">Loading options…</p>}
          {error && <p role="alert">Child T1D stage options are unavailable.</p>}
          <StageCheckboxList
            options={stageOptions}
            selected={snap.stages}
            onToggle={toggleStage}
            disabled={disabled}
          />
        </>
      )}
      {availability.age && (
        <>
          <div className="facet-subgroup__label">Child age</div>
          <RangeInputs
            ariaLabel="Child age"
            value={snap.age}
            onChange={(age) => commit({ ...snap, age })}
            disabled={disabled}
          />
        </>
      )}
      {availability.diagnosisAge && (
        <>
          <div className="facet-subgroup__label">Child age at diagnosis</div>
          <RangeInputs
            ariaLabel="Child age at diagnosis"
            value={snap.dx}
            onChange={(dx) => commit({ ...snap, dx })}
            disabled={disabled}
          />
        </>
      )}
    </fieldset>
  );
}

function RangeInputs({
  ariaLabel,
  value,
  onChange,
  disabled,
}: {
  ariaLabel: string;
  value: Range;
  onChange: (r: Range) => void;
  disabled?: boolean;
}) {
  return (
    <div className="numeric-range__inputs">
      <input
        type="number"
        inputMode="numeric"
        min={0}
        placeholder="min"
        aria-label={`${ariaLabel} minimum`}
        value={value.min}
        onChange={(e) => onChange({ ...value, min: e.target.value })}
        disabled={disabled}
      />
      <span className="numeric-range__sep" aria-hidden="true">–</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        placeholder="max"
        aria-label={`${ariaLabel} maximum`}
        value={value.max}
        onChange={(e) => onChange({ ...value, max: e.target.value })}
        disabled={disabled}
      />
    </div>
  );
}
